import { useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { IsoDate } from "helpers/types/dates"
import { computeExperimentSegmentToastMessage } from "../helpers/segment-filters"
import { SegmentLabelInput, useStrippedSegmentHeaderLabels } from "./use-segment-header-label"

export interface AffectedSegmentColumn extends SegmentLabelInput {
  name: string
}

interface PendingToast {
  portfolioId: string
  columns: AffectedSegmentColumn[]
  hasCleared: boolean
}

const EMPTY_COLUMNS: AffectedSegmentColumn[] = []

// One toast per page portfolio until the affected columns clear
export default function useUnsupportedSegmentToast(
  affectedColumns: AffectedSegmentColumn[] | null,
  pagePortfolioId: string | undefined,
  currentDate: IsoDate,
  currency: string,
): void {
  const dispatch = useDispatch()
  const [pending, setPending] = useState<PendingToast | null>(null)
  const firedColumnsRef = useRef<AffectedSegmentColumn[] | null>(null)

  // Snapshot at first sight: the strip empties affectedColumns before the labels can be ready; null is not-parsed-yet, not cleared
  if (pagePortfolioId && affectedColumns) {
    if (affectedColumns.length > 0) {
      if (!pending || pending.portfolioId !== pagePortfolioId || pending.hasCleared) {
        setPending({ portfolioId: pagePortfolioId, columns: [...affectedColumns], hasCleared: false })
      }
    } else if (pending && !pending.hasCleared) {
      setPending({ ...pending, hasCleared: true })
    }
  }

  const { labels, isReady } = useStrippedSegmentHeaderLabels(pending?.columns ?? EMPTY_COLUMNS, currentDate, currency)

  useEffect(() => {
    if (!pending || pending.portfolioId !== pagePortfolioId || !isReady || firedColumnsRef.current === pending.columns) {
      return
    }

    firedColumnsRef.current = pending.columns
    dispatch(NotificationCreators.add("warning", null, computeExperimentSegmentToastMessage(
      pending.columns.map(({ name }, i) => ({ name, label: labels[i] })),
    )))
  }, [pending, labels, isReady, pagePortfolioId, dispatch])
}
